import {
  DashboardState,
  EDatasourceType,
  IAiActionInfo,
  ESdChangeReason,
  IActionDefinitionDto,
  IActionParameter,
  IDatabaseTableModel,
  IDatasourceDto,
  IOfflineTable,
  LeftPanelType,
  RightPanelType
} from "@kuika/kuika-cl-model";
import { Guid } from "guid-typescript";
import {
  DatasourceCreateIntent,
  DatasourcesCentreView
} from "../../../components/design-tools/data-diagram/datasources-landing-model";
import {
  BrowseDataRoute,
  ITableBrowseRequest,
  ITableEditorView,
  routeBrowseData
} from "../../../components/design-tools/data-diagram/table-editor-view";
import { DatasourceService } from "../../../services/datasource-service";
import { notifyDesignerLiveToolIdle } from "../../../services/designer-live/designer-live";
import { DesignerPresenceReporter } from "../../../services/designer-live/designer-presence";
import { OfflineTablesService } from "../../../services/offline-tables-service";
import { PartialContextProvider } from "../../../shared/patial/partial-context-provider";
import { canDatasourceSupplyDocumentParameter } from "../../../shared/utility/action-definition-filter";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import { IScreenDesignContextState } from "../screen-design-context";
import { IScreenDesignContextProviderProps, ScreenDesignContextProvider } from "../screen-design-context-provider";

interface ITables {
  id: string;
  tables: IDatabaseTableModel[];
  datasourceId: string;
  // KOA-17026 — set of table names whose `columns` have been hydrated.
  // Slim path (`getTableIds`) populates `tables` with empty `columns: []`
  // sentinels and leaves this set empty. Consumers that need column metadata
  // call `ensureTableColumnsLoaded(dsId, names)` to fill the missing columns;
  // membership in this set is the authoritative "are columns loaded?" flag
  // because the empty sentinel is indistinguishable from a truly column-less
  // table. Reset by `refreshTableIds`.
  loadedColumnsForTables: Set<string>;
}

interface ITable {
  id?: string;
  key: string;
  title: string;
  columns: any;
  dropped: boolean;
}

export interface DatasourceProviderState {
  datasources: IDatasourceDto[];
  isSchemasLoading: boolean;
  schemas: Map<string, ITables>;
  setActiveCreatingDatasourceType: (dsType: EDatasourceType | undefined) => void;
  activeCreatingDatasourceType?: EDatasourceType;
  setActiveCreatingDatasourceName: (dsType?: EDatasourceType, name?: string) => void;
  activeCreatingDatasourceName?: string;
  setActiveCreatingCSharpCode: (code: string, value: EDatasourceType) => void;
  activeCreatingCSharpCode?: string;
  activeCreatingCSharpType?: EDatasourceType;
  setActiveCreatingActionName: (actionName: string) => void;
  activeCreatingActionName?: string;
  setActiveCreatingTableName: (tableName: string) => void;
  activeCreatingTableName?: string;
  /**
   * Which surface the Datasources tool's centre shows: its landing list, or the ER diagram.
   * Provider state rather than the landing's own, because the left tree's "+" has to put the
   * user back on the list — the tree cannot reach a component's local state.
   */
  datasourcesCentreView: DatasourcesCentreView;
  setDatasourcesCentreView: (view: DatasourcesCentreView) => void;
  /**
   * Set when something outside the landing asks for a datasource to be created — the tree's
   * "+", which used to open the create screen directly. The landing opens its connector sheet
   * on the requested card and clears this; nothing else reads it.
   */
  datasourceCreateIntent?: DatasourceCreateIntent;
  requestDatasourceCreate: (intent: DatasourceCreateIntent | undefined) => void;
  setActiveEditingDatasource: (ds: IDatasourceDto | undefined, dsIndex?: number) => void;
  activeEditingDatasource?: IDatasourceDto;
  activeEditingDatasourceIndex?: number;
  setEditingActionsDatasource: (
    ds: IDatasourceDto | undefined,
    action: IActionDefinitionDto | undefined,
    isCreating: boolean,
    openWithQueryBuilder: boolean,
    folderId?: string,
    creatingActionName?: string,
    isGenericSelectAction?: boolean
  ) => void;
  isGenericSelectActionCreating: boolean;
  setGenericSelectActionCreatingStatus: (status: boolean) => void;
  creatingActionsDatasource?: IDatasourceDto;
  openActionWithQueryBuilder?: boolean;
  editingAction?: IActionDefinitionDto;
  actionScript?: string;
  setActionScript: (table: any | undefined, scriptType: string, datasourceType: EDatasourceType | undefined) => void;
  setEditingTablesDatasource: (
    ds: IDatasourceDto | undefined,
    table: any | undefined,
    isCreating: boolean,
    creatingActionName?: string,
    isEditingDefaultKuikaTable?: boolean,
    view?: ITableEditorView
  ) => void;
  creatingTablesDatasource?: IDatasourceDto;
  editingTable?: any;
  isEditingDefaultKuikaTable?: boolean;
  /**
   * Which tab the open table editor was asked to show — Browse Data lands on its Data tab. A request, not the
   * tab itself: the editor owns its tab and switches on each new `requestId`. Cleared with the editing state.
   */
  tableEditorView?: ITableEditorView;
  // KOA-5857 — Supabase-style row editor in the data-diagram canvas.
  // setBrowsingRowsTable(ds, table) makes the canvas render the row grid
  // for that table; passing both as undefined returns to the schema view.
  browsingRowsTable?: any;
  browsingRowsDatasource?: IDatasourceDto;
  setBrowsingRowsTable: (ds: IDatasourceDto | undefined, table: ITableBrowseRequest | undefined) => void;
  cancelCreatingOrEditingDatasource: () => void;
  clearCSharpTempValues: () => void;
  getTables: (dsId: string) => void;
  refreshTables: () => void;
  // KOA-17026 — slim variants of getTables / refreshTables that skip column
  // metadata. reload_datasources should use these so it doesn't pull
  // multi-MB schema payloads on every refresh.
  getTableIds: (dsId: string) => Promise<void>;
  refreshTableIds: () => Promise<void>;
  ensureTableColumnsLoaded: (dsId: string, tableNames: string[]) => Promise<void>;
  // Savers await it before their editor closes: Edit reopens an editor with the row from this list,
  // so closing earlier lets a quick reopen show, and re-save, the code from before the save.
  refreshDatabases: () => Promise<void>;
  refreshActions: () => void;
  refreshActionParameters: (actionParameters: IActionParameter[]) => void;
  actionParameters: IActionParameter[];
  refreshActionStatement: (actionStatement: string) => void;
  actionStatement: string;
  creatingDatasouceFolderId?: string;
  lastAddedActionId?: string;
  lastAddedActionDsId?: string;
  setLastAddedAction: (actionId: string | undefined, dsId: string | undefined) => void;
  lastAddedTableId?: string;
  lastAddedTableDsId?: string;
  setLastAddedTable: (tableId: string | undefined, dsId: string | undefined) => void;
  actionToEditOnActionPanel?: IActionDefinitionDto;
  setActionToEditOnActionPanel: (action: IActionDefinitionDto | undefined) => void;
  offlineTables: IOfflineTable[];
  refresh3rdPartyDsSchema: (datasourceId: string, tables: Array<IDatabaseTableModel>) => void;
  editingSessionVariables: boolean;
  setEditingSessionVariables: (value: boolean) => void;
  updateActionDefinitions: (actionDefinitions: IActionDefinitionDto[]) => void;
  /**
   * Whether the action editor currently open belongs to the Document Parameters tool rather than to the data
   * diagram (KOA-17254). Recorded when the editor is opened, not re-derived while it is on screen — see
   * `enterDatasourceEditingSurface`.
   *
   * It describes the editor, not the current tool, so it deliberately survives a tool switch and is cleared only
   * with the editing state itself. Every surface that reads it is already scoped to `documentParameters`, so a
   * claim held elsewhere is inert — while clearing it on navigation would break the query builder, which the
   * editor opens as an overlay and comes back from.
   */
  isEditingDocumentParameterSourceAction: boolean;
}

export class DatasourceProvider extends PartialContextProvider<
  ScreenDesignContextProvider,
  IScreenDesignContextProviderProps,
  IScreenDesignContextState,
  DatasourceProviderState
> {
  private schemas: Map<string, ITables> = new Map();

  /** Numbers each table-editor tab request, so asking for the same tab twice is still a new request. */
  private tableEditorViewSeq: number = 0;

  static defaultState: DatasourceProviderState = {
    datasources: [],
    isSchemasLoading: false,
    schemas: new Map(),
    setActiveCreatingDatasourceType: () => {},
    activeCreatingDatasourceType: undefined,
    setActiveCreatingDatasourceName: () => {},
    activeCreatingDatasourceName: undefined,
    setActiveCreatingCSharpCode: () => {},
    activeCreatingCSharpCode: undefined,
    activeCreatingCSharpType: undefined,
    setActiveCreatingActionName: () => {},
    activeCreatingActionName: undefined,
    setActiveCreatingTableName: () => {},
    activeCreatingTableName: undefined,
    datasourcesCentreView: "list",
    setDatasourcesCentreView: () => {},
    datasourceCreateIntent: undefined,
    requestDatasourceCreate: () => {},
    setActiveEditingDatasource: () => {},
    activeEditingDatasource: undefined,
    activeEditingDatasourceIndex: undefined,
    setEditingActionsDatasource: () => {},
    isGenericSelectActionCreating: false,
    setGenericSelectActionCreatingStatus: () => {},
    creatingActionsDatasource: undefined,
    openActionWithQueryBuilder: undefined,
    editingAction: undefined,
    actionScript: undefined,
    setActionScript: () => {},
    setEditingTablesDatasource: () => {},
    creatingTablesDatasource: undefined,
    editingTable: undefined,
    isEditingDefaultKuikaTable: false,
    tableEditorView: undefined,
    browsingRowsTable: undefined,
    browsingRowsDatasource: undefined,
    setBrowsingRowsTable: () => {},
    cancelCreatingOrEditingDatasource: () => {},
    clearCSharpTempValues: () => {},
    getTables: () => {},
    refreshTables: () => {},
    getTableIds: () => Promise.resolve(),
    refreshTableIds: () => Promise.resolve(),
    ensureTableColumnsLoaded: () => Promise.resolve(),
    refreshDatabases: async (): Promise<void> => {},
    refreshActions: () => {},
    refreshActionParameters: () => {},
    actionParameters: [],
    refreshActionStatement: () => {},
    actionStatement: "",
    creatingDatasouceFolderId: undefined,
    lastAddedActionId: undefined,
    lastAddedActionDsId: undefined,
    setLastAddedAction: () => {},
    lastAddedTableId: undefined,
    lastAddedTableDsId: undefined,
    setLastAddedTable: () => {},
    actionToEditOnActionPanel: undefined,
    setActionToEditOnActionPanel: () => {},
    offlineTables: [],
    refresh3rdPartyDsSchema: () => {},
    editingSessionVariables: false,
    setEditingSessionVariables: () => {},
    updateActionDefinitions: () => {},
    isEditingDocumentParameterSourceAction: false
  };

  getProviderValues = (): DatasourceProviderState => {
    return {
      datasources: this.state.datasources,
      isSchemasLoading: this.state.isSchemasLoading,
      schemas: this.schemas,
      setActiveCreatingDatasourceType: this.setActiveCreatingDatasourceType,
      activeCreatingDatasourceType: this.state.activeCreatingDatasourceType,
      setActiveCreatingDatasourceName: this.setActiveCreatingDatasourceName,
      activeCreatingDatasourceName: this.state.activeCreatingDatasourceName,
      setActiveCreatingCSharpCode: this.setActiveCreatingCSharpCode,
      activeCreatingCSharpCode: this.state.activeCreatingCSharpCode,
      activeCreatingCSharpType: this.state.activeCreatingCSharpType,
      setActiveCreatingActionName: this.setActiveCreatingActionName,
      activeCreatingActionName: this.state.activeCreatingActionName,
      setActiveCreatingTableName: this.setActiveCreatingTableName,
      activeCreatingTableName: this.state.activeCreatingTableName,
      datasourcesCentreView: this.state.datasourcesCentreView,
      setDatasourcesCentreView: this.setDatasourcesCentreView,
      datasourceCreateIntent: this.state.datasourceCreateIntent,
      requestDatasourceCreate: this.requestDatasourceCreate,
      setActiveEditingDatasource: this.setActiveEditingDatasource,
      activeEditingDatasource: this.state.activeEditingDatasource,
      activeEditingDatasourceIndex: this.state.activeEditingDatasourceIndex,
      setEditingActionsDatasource: this.setEditingActionsDatasource,
      isGenericSelectActionCreating: this.state.isGenericSelectActionCreating,
      setGenericSelectActionCreatingStatus: this.setGenericSelectActionCreatingStatus,
      creatingActionsDatasource: this.state.creatingActionsDatasource,
      openActionWithQueryBuilder: this.state.openActionWithQueryBuilder,
      editingAction: this.state.editingAction,
      actionScript: this.state.actionScript,
      setActionScript: this.setActionScript,
      setEditingTablesDatasource: this.setEditingTablesDatasource,
      creatingTablesDatasource: this.state.creatingTablesDatasource,
      editingTable: this.state.editingTable,
      isEditingDefaultKuikaTable: this.state.isEditingDefaultKuikaTable,
      tableEditorView: this.state.tableEditorView,
      browsingRowsTable: this.state.browsingRowsTable,
      browsingRowsDatasource: this.state.browsingRowsDatasource,
      setBrowsingRowsTable: this.setBrowsingRowsTable,
      cancelCreatingOrEditingDatasource: this.cancelCreatingOrEditingDatasource,
      clearCSharpTempValues: this.clearCSharpTempValues,
      getTables: this.getTables,
      refreshTables: this.refreshTables,
      getTableIds: this.getTableIds,
      refreshTableIds: this.refreshTableIds,
      ensureTableColumnsLoaded: this.ensureTableColumnsLoaded,
      refreshDatabases: this.refreshDatabases,
      refreshActions: this.refreshActions,
      refreshActionParameters: this.refreshActionParameters,
      actionParameters: this.state.actionParameters,
      refreshActionStatement: this.refreshActionStatement,
      actionStatement: this.state.actionStatement,
      creatingDatasouceFolderId: this.state.creatingDatasouceFolderId,
      lastAddedActionId: this.state.lastAddedActionId,
      lastAddedActionDsId: this.state.lastAddedActionDsId,
      setLastAddedAction: this.setLastAddedAction,
      lastAddedTableId: this.state.lastAddedTableId,
      lastAddedTableDsId: this.state.lastAddedTableDsId,
      setLastAddedTable: this.setLastAddedTable,
      actionToEditOnActionPanel: this.state.actionToEditOnActionPanel,
      setActionToEditOnActionPanel: this.setActionToEditOnActionPanel,
      offlineTables: this.state.offlineTables,
      refresh3rdPartyDsSchema: this.refresh3rdPartyDsSchema,
      editingSessionVariables: this.state.editingSessionVariables,
      setEditingSessionVariables: this.setEditingSessionVariables,
      updateActionDefinitions: this.updateActionDefinitions,
      isEditingDocumentParameterSourceAction: this.state.isEditingDocumentParameterSourceAction
    };
  };

  setActiveCreatingDatasourceType = (dsType: EDatasourceType | undefined) => {
    this.cancelCreatingOrEditingDatasource();
    this.partial.designSituationProvider.setActiveRightPanelByClick(RightPanelType.Properties);
    if (dsType === undefined) {
      this.setState({ activeCreatingDatasourceType: undefined });
    } else {
      this.setState({ activeCreatingDatasourceType: dsType });
    }
  };

  setActiveCreatingDatasourceName = (dsType?: EDatasourceType, name?: string) => {
    this.setState({ activeCreatingDatasourceName: name });
    if (dsType) this.setActiveCreatingDatasourceType(dsType);
  };

  setDatasourcesCentreView = (view: DatasourcesCentreView) => {
    this.setState({ datasourcesCentreView: view }, () => {
      // The diagram is drawn from `databaseDiagramData`, which only this refresh fills. The old
      // create screen fired it on its way out for the same reason; showing the diagram is now
      // the moment that matters.
      if (view === "diagram") {
        this.partial.designSituationProvider.updateDatabaseDiagramData();
      }
    });
  };

  requestDatasourceCreate = (intent: DatasourceCreateIntent | undefined) => {
    this.setState({
      datasourceCreateIntent: intent,
      datasourcesCentreView: intent ? "list" : this.state.datasourcesCentreView
    });
  };

  /**
   * Guarantees a surface that renders whatever editor is about to be opened.
   *
   * <b>The invariant.</b> Every piece of datasource editing state in this partition — `activeEditingDatasource`,
   * `editingAction`, `editingTable`, `creatingActionsDatasource`, `editingSessionVariables`, `browsingRowsTable` —
   * has exactly one renderer: `DataDiagramCanvas`, mounted only for `dataDiagramScheme` and `aiHub`. Setting the
   * state without that canvas mounted is not a smaller version of opening an editor; it is opening nothing while
   * telling the rest of the designer that an editor is open.
   *
   * <b>Why it has to live here.</b> The datasources tree used to be exclusive to the data diagram, so its Edit and
   * New items could assume they were already on the right surface. Document Parameters then adopted that tree as
   * its own left panel, and every one of those assumptions became false at once: Edit on a datasource, a table or
   * an action left the previous tool on screen with no editor and no error, and the invisible unsaved-changes
   * guard then fought whatever the user did next. Every surface that opens these editors from *outside* the tree
   * already navigates first — the footer's error links, the custom-actions list, the MCP view actions — so the
   * rule existed; it was just a convention each new call site had to remember. Here it is the invariant.
   *
   * <b>The one exception is claimed, not guessed.</b> Document Parameters hosts the database action editor itself
   * so a parameter's source can be authored without losing the tool, and `claimsDocumentParameterSourceAction`
   * decides that at click time. The answer is stored rather than re-derived from the editing state, because the
   * editing state cannot tell the two cases apart: a SQL action opened from the tree to be edited on its own looks
   * exactly like one opened to supply a `<TAG>`. Re-deriving it is what left the tool's own right panel standing
   * beside an unrelated action editor, and what bounced a table Edit back to the parameter table.
   */
  private enterDatasourceEditingSurface = (claimedByDocumentParameters: boolean = false): void => {
    this.setState({ isEditingDocumentParameterSourceAction: claimedByDocumentParameters });
    if (claimedByDocumentParameters) return;

    const current: DashboardState = this.state.dashboardState;
    if (current === DashboardState.dataDiagramScheme || current === DashboardState.aiHub) return;
    this.partial.designSituationProvider.setDashboardState(DashboardState.dataDiagramScheme);
  };

  /**
   * Whether the Document Parameters tool is hosting this action editor itself.
   *
   * The tool's whole job is saying where a `<TAG>` gets its value, and only a database query can supply one — so
   * authoring one of those is part of that job and does not deserve a trip to another tool. Everything else the
   * tree offers from the same menus (an AI, SAP, Blockly or Vision action, a connection, a table, session
   * variables) belongs to the datasources tool, has no editor on this canvas, and navigates.
   *
   * A brand new action is judged by its datasource: it has no `actionType` yet, and rejecting it would close the
   * only path the tool has for authoring a source that does not exist yet.
   */
  private claimsDocumentParameterSourceAction = (
    ds: IDatasourceDto | undefined,
    action: IActionDefinitionDto | undefined,
    isCreating: boolean
  ): boolean => {
    if (this.state.dashboardState !== DashboardState.documentParameters) return false;
    if (!canDatasourceSupplyDocumentParameter(ds?.datasourceType)) return false;
    return isCreating || canDatasourceSupplyDocumentParameter(action?.actionType);
  };

  setActiveEditingDatasource = (ds: IDatasourceDto | undefined, dsIndex?: number) => {
    this.cancelCreatingOrEditingDatasource();
    // Only on the way in: passing `undefined` closes the editor, and navigating to the data diagram to show
    // nothing would turn every cancel into a tool switch.
    if (ds !== undefined) this.enterDatasourceEditingSurface();
    this.partial.designSituationProvider.setActiveRightPanelByClick(RightPanelType.Comments);
    if (ds === undefined) {
      this.setState({ activeEditingDatasource: undefined, activeEditingDatasourceIndex: undefined });
    } else {
      this.setState({ activeEditingDatasource: ds, activeEditingDatasourceIndex: dsIndex });
      DesignerPresenceReporter.reportEntity("datasource", ds.id, ds.name, "datasource.changed");
    }
  };

  setEditingActionsDatasource = (
    ds: IDatasourceDto | undefined,
    action: IActionDefinitionDto | undefined,
    isCreating: boolean,
    openWithQueryBuilder: boolean,
    folderId?: string,
    creatingActionName?: string,
    isGenericSelectAction?: boolean
  ) => {
    const claimedByDocumentParameters: boolean = this.claimsDocumentParameterSourceAction(ds, action, isCreating);
    this.cancelCreatingOrEditingDatasource();
    if (ds !== undefined || action !== undefined) this.enterDatasourceEditingSurface(claimedByDocumentParameters);
    this.partial.designSituationProvider.setActiveRightPanelByClick(RightPanelType.Properties);
    // Always set the folder ID, even if undefined, to clear previous state
    this.setState({ creatingDatasouceFolderId: folderId });
    if (isCreating) {
      this.setState({
        creatingActionsDatasource: ds,
        editingAction: undefined,
        openActionWithQueryBuilder: openWithQueryBuilder,
        activeCreatingActionName: creatingActionName,
        isCreateActionPanelActive: false,
        isGenericSelectActionCreating: isGenericSelectAction
      });
    } else {
      // For AI action edits, parse aiActionInfo immediately so the right panel
      // is visible from the first render (activeAIActionInfoForRightPanel drives
      // the RightPanel visibility check in design-situation-provider)
      let parsedAiInfo: IAiActionInfo | undefined;
      if (ds?.datasourceType === EDatasourceType.AIActions && action?.aiActionInfo) {
        try {
          parsedAiInfo = JSON.parse((action.aiActionInfo as unknown as string) || "") as IAiActionInfo;
        } catch {
          // invalid JSON – handleLoadAction will retry
        }
      }
      this.setState({
        editingAction: action,
        creatingActionsDatasource: ds,
        openActionWithQueryBuilder: openWithQueryBuilder,
        ...(parsedAiInfo
          ? { activeAIActionInfoForRightPanel: parsedAiInfo, actionParameters: action?.actionParameters ?? [] }
          : {})
      });
    }
    if (ds === undefined && action === undefined) {
      this.setState({ creatingActionsDatasource: undefined, editingAction: undefined });
    } else if (!isCreating && action) {
      DesignerPresenceReporter.reportEntity("action", action.id, action.name, "action.changed");
    }
  };

  setGenericSelectActionCreatingStatus = (status: boolean) => {
    this.setState({ isGenericSelectActionCreating: status });
  };

  /**
   * Browse Data. Where the table editor can host the rows it opens on its Data tab — retargeting the open editor
   * when it already holds the table, rather than tearing it down — and the standalone row browser takes the rest
   * (`routeBrowseData`). Passing no table closes the row browser.
   */
  setBrowsingRowsTable = (ds: IDatasourceDto | undefined, table: ITableBrowseRequest | undefined) => {
    if (!ds || !table) {
      this.cancelCreatingOrEditingDatasource();
      return;
    }

    const view: ITableEditorView = {
      tab: "data",
      initialFilter: table._initialFilter,
      requestId: ++this.tableEditorViewSeq
    };
    // The schema map is served from this instance, not from the state.
    const route: BrowseDataRoute = routeBrowseData({ ...this.state, schemas: this.schemas }, ds, table);
    if (route.kind === "same-table") {
      // No cancel: that would clear `editingTable` and close the editor this only has to switch.
      this.enterDatasourceEditingSurface();
      this.setState({ tableEditorView: view });
      return;
    }
    if (route.kind === "table-editor") {
      this.setEditingTablesDatasource(ds, route.table, false, undefined, route.readOnly, view);
      return;
    }
    this.cancelCreatingOrEditingDatasource();
    this.enterDatasourceEditingSurface();
    this.setState({ browsingRowsTable: table, browsingRowsDatasource: ds });
  };

  setEditingTablesDatasource = (
    ds: IDatasourceDto | undefined,
    table: any | undefined,
    isCreating: boolean,
    creatingTableName?: string,
    isEditingDefaultKuikaTable?: boolean,
    view?: ITableEditorView
  ) => {
    this.cancelCreatingOrEditingDatasource();
    if (ds !== undefined || table !== undefined) this.enterDatasourceEditingSurface();
    this.partial.designSituationProvider.setActiveRightPanelByClick(RightPanelType.Properties);
    if (isCreating) {
      this.setState({
        creatingTablesDatasource: ds,
        editingTable: undefined,
        activeCreatingTableName: creatingTableName
      });
    } else {
      // The tab request rides the same write as the table: the editor mounts for the table and reads its first
      // tab from it, so a request that landed a render later would find an editor already on Columns.
      this.setState({
        editingTable: table,
        creatingTablesDatasource: ds,
        isEditingDefaultKuikaTable,
        tableEditorView: view
      });
    }
    if (ds === undefined && table === undefined) {
      this.setState({
        creatingTablesDatasource: undefined,
        editingTable: undefined,
        isEditingDefaultKuikaTable: undefined
      });
    }
  };

  setActiveCreatingActionName = (name: string) => {
    this.setState({ activeCreatingActionName: name });
  };

  setActiveCreatingTableName = (name: string) => {
    this.setState({ activeCreatingTableName: name });
  };

  cancelCreatingOrEditingDatasource = () => {
    // Synchronous report so a setter that re-opens another entity right after wins the
    // merged presence report; the idle notification waits for the committed state.
    DesignerPresenceReporter.reportEntity("datasource", undefined, undefined);
    this.setState(
      {
        creatingTablesDatasource: undefined,
        editingTable: undefined,
        isEditingDefaultKuikaTable: undefined,
        tableEditorView: undefined,
        activeEditingDatasource: undefined,
        activeEditingDatasourceIndex: undefined,
        activeCreatingDatasourceType: undefined,
        creatingActionsDatasource: undefined,
        isGenericSelectActionCreating: undefined,
        editingAction: undefined,
        actionScript: undefined,
        lastAddedActionId: undefined,
        lastAddedActionDsId: undefined,
        lastAddedTableId: undefined,
        lastAddedTableDsId: undefined,
        actionParameters: undefined,
        openActionWithQueryBuilder: false,
        activeCreatingActionName: undefined,
        activeCreatingTableName: undefined,
        editingSessionVariables: false,
        activeAIActionInfoForRightPanel: undefined,
        browsingRowsTable: undefined,
        browsingRowsDatasource: undefined,
        // Cleared with the state it describes. Every setter that opens an editor cancels the previous one through
        // here first, so a claim can never outlive the editor it was made for and be inherited by the next.
        isEditingDocumentParameterSourceAction: false
      },
      () => notifyDesignerLiveToolIdle()
    );
  };

  setActiveCreatingCSharpCode = (code: string, value: EDatasourceType) => {
    this.setState({ activeCreatingCSharpCode: code, activeCreatingCSharpType: value });
  };

  clearCSharpTempValues = () => {
    this.setState({ activeCreatingCSharpCode: undefined, activeCreatingCSharpType: undefined });
  };

  getTables = async (dsId: string) => {
    try {
      const response = await DatasourceService.GetSchema(dsId);
      if (response.status !== 200 || !response.data.tables?.length) {
        return;
      }

      const { tables } = response.data;

      const existingSchema = this.schemas.get(dsId);

      const transformedTables = tables.map((tbl) => ({
        id: tbl.id,
        key: Guid.create().toString(),
        name: tbl.name,
        schema: tbl.schema,
        title: tbl.name,
        columns: tbl.columns,
        dropped: tbl.dropped
      }));

      // KOA-17026 — full path hydrates every table's columns, so mark all of
      // them as loaded for the lazy-load contract.
      const loadedNames = new Set(transformedTables.map((t) => t.name));

      if (existingSchema) {
        this.schemas.set(dsId, {
          ...existingSchema,
          tables: transformedTables,
          loadedColumnsForTables: loadedNames
        });
      } else {
        this.schemas.set(dsId, {
          id: Guid.create().toString(),
          datasourceId: dsId,
          tables: transformedTables,
          loadedColumnsForTables: loadedNames
        });
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    }
  };

  // KOA-17026 — slim list refresh for a single datasource. Populates `tables`
  // with id/name/schema only; `columns` stays as `[]` sentinels and
  // `loadedColumnsForTables` is empty until a consumer calls
  // `ensureTableColumnsLoaded`. Designed for `reload_datasources` which
  // must not pull column metadata for every datasource on every refresh.
  getTableIds = async (dsId: string): Promise<void> => {
    try {
      const response = await DatasourceService.GetTableIds(dsId);
      if (response.status !== 200 || !response.data?.tables) {
        return;
      }
      const transformed = response.data.tables.map((tbl) => ({
        id: tbl.id,
        key: Guid.create().toString(),
        name: tbl.name,
        schema: tbl.schema,
        title: tbl.name,
        columns: [],
        dropped: false
      }));

      const existingSchema = this.schemas.get(dsId);
      if (existingSchema) {
        this.schemas.set(dsId, {
          ...existingSchema,
          tables: transformed,
          loadedColumnsForTables: new Set()
        });
      } else {
        this.schemas.set(dsId, {
          id: Guid.create().toString(),
          datasourceId: dsId,
          tables: transformed,
          loadedColumnsForTables: new Set()
        });
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    }
  };

  // KOA-17026 — slim variant of refreshTables. Hits the slim list endpoint
  // for every datasource the cache currently knows about, in parallel.
  // `reload_datasources` calls this instead of refreshTables to keep the
  // schema fetch under the browser body-timeout on multi-table tenants.
  refreshTableIds = async (): Promise<void> => {
    const datasourceIds = Array.from(this.schemas.entries()).map(([_, schema]) => schema.datasourceId);
    if (datasourceIds.length === 0) {
      return;
    }
    try {
      this.setState({ isSchemasLoading: true });
      const promises: Promise<void>[] = [
        this.refreshOfflineTables(),
        ...datasourceIds.map((dsId) => this.getTableIds(dsId))
      ];
      await Promise.all(promises);
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    } finally {
      this.setState({ isSchemasLoading: false });
    }
  };

  // KOA-17026 — on-demand detail fetch. Called by consumers (sql-action
  // editor, managed-db save panel, etc.) right before they read
  // `tables[].columns`. Skips the network round-trip when every requested
  // name is already in `loadedColumnsForTables` for the given datasource.
  // The detail call is batched: a single backend round-trip carries the
  // missing names list, then the merged response patches `columns` for
  // each table in place.
  ensureTableColumnsLoaded = async (dsId: string, tableNames: string[]): Promise<void> => {
    if (!tableNames || tableNames.length === 0) return;
    const existingSchema = this.schemas.get(dsId);
    if (!existingSchema) {
      // No slim list yet — pull it before fetching column detail. The slim
      // call is cheap; this only fires when a consumer races ahead of
      // reload_datasources, which is fine.
      await this.getTableIds(dsId);
    }
    const schemaAfterSlim = this.schemas.get(dsId);
    if (!schemaAfterSlim) return;
    const missing = tableNames.filter((name) => !schemaAfterSlim.loadedColumnsForTables.has(name));
    if (missing.length === 0) return;
    try {
      const response = await DatasourceService.GetSchemaForTables(dsId, missing);
      if (response.status !== 200 || !response.data?.tables) return;
      const detailByName = new Map<string, IDatabaseTableModel>();
      for (const tbl of response.data.tables) {
        detailByName.set(tbl.name, tbl);
      }
      const updatedTables = schemaAfterSlim.tables.map((tbl) => {
        const detail = detailByName.get(tbl.name);
        if (!detail) return tbl;
        return { ...tbl, columns: detail.columns, dropped: detail.dropped };
      });
      const newLoaded = new Set(schemaAfterSlim.loadedColumnsForTables);
      for (const name of missing) {
        if (detailByName.has(name)) newLoaded.add(name);
      }
      this.schemas.set(dsId, {
        ...schemaAfterSlim,
        tables: updatedTables,
        loadedColumnsForTables: newLoaded
      });
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    }
  };

  refresh3rdPartyDsSchema = (datasourceId: string, tables: IDatabaseTableModel[]) => {
    if (!tables?.length) {
      return;
    }

    this.setState((prevState) => {
      const existingSchema = this.schemas.get(datasourceId);
      const transformedTables = tables.map((tbl) => ({
        id: tbl.id,
        key: Guid.create().toString(),
        name: tbl.name,
        schema: tbl.schema,
        title: tbl.name,
        columns: tbl.columns,
        dropped: tbl.dropped
      }));
      // KOA-17026 — caller passes full table payload (columns hydrated), so
      // mark every entry as loaded for the lazy-load contract.
      const loadedNames = new Set(transformedTables.map((t) => t.name));

      if (!existingSchema) {
        this.schemas.set(datasourceId, {
          id: Guid.create().toString(),
          datasourceId,
          tables: transformedTables,
          loadedColumnsForTables: loadedNames
        });
      } else {
        this.schemas.set(datasourceId, {
          ...existingSchema,
          tables: transformedTables,
          loadedColumnsForTables: loadedNames
        });
      }
    });
  };

  refreshTables = async (excludedDatasourceIds?: string[]) => {
    const datasourceIds = new Set(
      Array.from(this.schemas.entries())
        .filter(([_, schema]) => !excludedDatasourceIds?.includes(schema.datasourceId))
        .map(([_, schema]) => schema.datasourceId)
    );

    if (datasourceIds.size === 0) {
      return;
    }

    try {
      this.setState({ isSchemasLoading: true });
      const promises = [this.refreshOfflineTables(), ...Array.from(datasourceIds).map((dsId) => this.getTables(dsId))];

      await Promise.all(promises);
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    } finally {
      this.setState({ isSchemasLoading: false });
    }
  };

  refreshOfflineTables = async () => {
    try {
      const res = await OfflineTablesService.getAll();
      if (res.status === 200) {
        this.setState({ offlineTables: res.data });
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    }
  };

  refreshDatabases = async (): Promise<void> => {
    try {
      const response = await DatasourceService.GetDatasourcesOfDomain();
      if (response.status === 200) {
        let datasources = [...(response.data as IDatasourceDto[])];
        // Add synthetic AI Agent datasource if it doesn't exist (same logic as loadActionDefinitions)
        if (!datasources.find((ds) => ds.datasourceType === EDatasourceType.AIAgent) && !KMainFunctions.isMainAI()) {
          datasources.push({
            datasourceType: EDatasourceType.AIAgent,
            id: "",
            name: "AIAgents"
          } as IDatasourceDto);
        }
        if (!datasources.find((ds) => ds.datasourceType === EDatasourceType.VisionAction)) {
          datasources.push({
            datasourceType: EDatasourceType.VisionAction,
            id: "",
            name: "VisionActions"
          } as IDatasourceDto);
        }
        this.setState({ datasources, changeReason: ESdChangeReason.OtherSetStates });
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    }
  };

  refreshActions = async () => {
    try {
      const response = await DatasourceService.GetActionsByDomain();
      if (response.status === 200) {
        const actions = response.data as IActionDefinitionDto[];
        this.setState({ actionDefinitions: actions });
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    }
  };

  refreshActionParameters = (actionParameters: IActionParameter[]) => {
    this.setState({ actionParameters });
  };

  refreshActionStatement = (actionStatement: string) => {
    this.setState({ actionStatement });
  };

  setActionScript = async (table: any | undefined, scriptType: string, datasourceType: EDatasourceType | undefined) => {
    const generateSqlScript = (
      table: any,
      scriptType: string,
      paramPrefix: string,
      quoteChar: string = "",
      useSchema: boolean = true
    ): string => {
      const tableName =
        useSchema && table.schema
          ? `${quoteChar}${table.schema}${quoteChar}.${quoteChar}${table.title}${quoteChar}`
          : `${quoteChar}${table.title}${quoteChar}`;

      if (scriptType === "select") {
        return `SELECT * FROM ${tableName}`;
      }

      if (scriptType === "selectById") {
        return `SELECT * FROM ${tableName} WHERE ${quoteChar}Id${quoteChar} = ${paramPrefix}Id`;
      }

      if (scriptType === "delete") {
        return `DELETE FROM ${tableName} WHERE ${quoteChar}Id${quoteChar} = ${paramPrefix}Id`;
      }

      if (scriptType === "empty") {
        return "";
      }

      let fields = table.columns.filter((x) => x.name !== "Id" && x.isIdentity !== true);

      if (scriptType === "update") {
        const fieldAssignments = fields
          .map((x) => `${quoteChar}${x.name}${quoteChar} = ${paramPrefix}${x.name}`)
          .join(", ");
        const fieldNames = fields.map((x) => `${quoteChar}${x.name}${quoteChar}`).join(", ");
        const values = fields.map((x) => `${paramPrefix}${x.name}`).join(", ");

        if (datasourceType === EDatasourceType.ManagedDbPostgre || datasourceType === EDatasourceType.Postgre) {
          return `UPDATE ${tableName}\nSET ${fieldAssignments}\nWHERE ${quoteChar}Id${quoteChar} = ${paramPrefix}Id;\n\nINSERT INTO ${tableName} (${quoteChar}Id${quoteChar}, ${fieldNames})\nSELECT ${paramPrefix}Id, ${values}\nWHERE NOT EXISTS (SELECT 1 FROM ${tableName} WHERE ${quoteChar}Id${quoteChar} = ${paramPrefix}Id);`;
        }

        if (datasourceType === EDatasourceType.ManagedDbOracle || datasourceType === EDatasourceType.Oracle) {
          return `DECLARE\n  v_rowcount INTEGER;\nBEGIN\n  UPDATE ${tableName}\n  SET ${fieldAssignments}\n  WHERE ${quoteChar}Id${quoteChar} = ${paramPrefix}Id;\n  v_rowcount := SQL%ROWCOUNT;\n  IF v_rowcount = 0 THEN\n    INSERT INTO ${tableName} (${quoteChar}Id${quoteChar}, ${fieldNames})\n    VALUES (${paramPrefix}Id, ${values});\n  END IF;\nEND;`;
        }

        return `UPDATE ${tableName}\nSET ${fieldAssignments}\nWHERE Id = ${paramPrefix}Id\nIF @@ROWCOUNT = 0\nBEGIN\n  INSERT INTO ${tableName} (Id, ${fieldNames})\n  VALUES (${paramPrefix}Id, ${values});\nEND`;
      }

      const stringFields = fields.filter((x) => {
        const sqlType = x.sqlDataType?.toLowerCase();
        return (
          sqlType === "nvarchar" || sqlType === "varchar" || sqlType === "character varying" || sqlType === "nvarchar2"
        );
      });

      if (!stringFields.length) {
        KMainFunctions.displayErrorNotification(
          "In order to add this SQL, you should choose a Table with at least 1 string column",
          { title: "No String column found" }
        );
        return "";
      }

      if (scriptType === "searchByKeyword") {
        const conditions = stringFields
          .map((x) => `${quoteChar}${x.name}${quoteChar} LIKE ${paramPrefix}keyword`)
          .join(" OR\n");
        return `SELECT * FROM ${tableName}\nWHERE ${conditions} OR\n${paramPrefix}keyword IS NULL\nORDER BY ${quoteChar}${stringFields[0].name}${quoteChar}`;
      }

      if (scriptType === "searchWithPagination") {
        const conditions = stringFields
          .map((x) => `${quoteChar}${x.name}${quoteChar} LIKE ${paramPrefix}${x.name}`)
          .join(" OR\n");

        if (datasourceType === EDatasourceType.ManagedDbPostgre || datasourceType === EDatasourceType.Postgre) {
          return `SELECT * FROM ${tableName}\nWHERE ${conditions}\nORDER BY ${quoteChar}${stringFields[0].name}${quoteChar}\nOFFSET (${paramPrefix}currentpage-1)*${paramPrefix}pagesize\nLIMIT ${paramPrefix}pagesize`;
        }

        return `SELECT * FROM ${tableName}\nWHERE ${conditions}\nORDER BY ${quoteChar}${stringFields[0].name}${quoteChar}\nOFFSET (${paramPrefix}currentpage-1)*${paramPrefix}pagesize\nROWS FETCH NEXT ${paramPrefix}pagesize\nROWS ONLY`;
      }

      return "";
    };

    if (!table || !scriptType) {
      if (scriptType === "empty") {
        this.setState({ actionScript: "" });
      }
      return;
    }

    let script = "";

    if (
      datasourceType === EDatasourceType.ManagedDb ||
      datasourceType === EDatasourceType.MsSql ||
      datasourceType === EDatasourceType.CustomDb
    ) {
      script = generateSqlScript(table, scriptType, "@", "", true);
    } else if (datasourceType === EDatasourceType.ManagedDbPostgre || datasourceType === EDatasourceType.Postgre) {
      script = generateSqlScript(table, scriptType, ":", '"', true);
    } else if (datasourceType === EDatasourceType.ManagedDbOracle || datasourceType === EDatasourceType.Oracle) {
      script = generateSqlScript(table, scriptType, ":", '"', false);
    } else {
      script = generateSqlScript(table, scriptType, "@", "", true);
    }

    if (script !== "") {
      this.setState({ actionScript: script });
    }
  };

  setLastAddedAction = (actionId?: string, dsId?: string) => {
    this.setState({ lastAddedActionId: actionId, lastAddedActionDsId: dsId });
  };

  setLastAddedTable = (tableId?: string, dsId?: string) => {
    this.setState({ lastAddedTableId: tableId, lastAddedTableDsId: dsId });
  };

  setActionToEditOnActionPanel = (action: IActionDefinitionDto | undefined) => {
    this.partial.designSituationProvider.setLeftPanelCollapsed(false);
    if (action) this.partial.designSituationProvider.setActiveLeftPanelByClick(LeftPanelType.Datasources);
    setTimeout(() => this.setState({ actionToEditOnActionPanel: action }), 300);
  };

  setEditingSessionVariables = (value: boolean) => {
    this.cancelCreatingOrEditingDatasource();
    if (value) this.enterDatasourceEditingSurface();
    this.setState({ editingSessionVariables: value });
  };

  updateActionDefinitions = (actionDefinitions: IActionDefinitionDto[]) => {
    this.setState({ actionDefinitions });
  };
}
